/**
 * ResetPassword — /reset-password?token=...
 *
 * Landing page for the reset link emailed by the forgot-password flow.
 * Design from the Claude Design auth handoff: strength meter, confirm
 * field with match validation, and a success state.
 */

import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { authApi } from '../services/api'
import AuthLayout from '../components/AuthLayout'
import {
  PasswordField, PrimaryButton, ErrorBanner, FormHeader, SuccessBadge,
  validators, CheckIcon, AlertIcon, ArrowLeft,
} from '../components/AuthFields'
import { JAI_COLORS, JAI_FONTS } from '../components/JaiBrand'

export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [pw, setPw] = useState('')
  const [confirm, setConfirm] = useState('')
  const [touched, setTouched] = useState({})
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [formErr, setFormErr] = useState('')

  // No token in URL — show a clear error instead of a broken form
  if (!token) {
    return (
      <AuthLayout screen="reset">
        <SuccessBadge icon={<AlertIcon size={26} stroke={JAI_COLORS.error} sw={1.8} />} />
        <FormHeader title="Invalid link" />
        <p style={{ fontSize: 13.5, color: JAI_COLORS.mute, margin: '-14px 0 0', fontFamily: JAI_FONTS.tight, lineHeight: 1.6 }}>
          This reset link is missing its token. Try clicking the link in your email again, or request a new one.
        </p>
        <div style={{ marginTop: 22 }}>
          <PrimaryButton type="button" onClick={() => navigate('/forgot-password')}>Request new link</PrimaryButton>
        </div>
        <p style={{ textAlign: 'center', fontSize: 12.5, marginTop: 16 }}>
          <button type="button" onClick={() => navigate('/login')}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: JAI_COLORS.blue, fontWeight: 600, fontSize: 12.5, fontFamily: JAI_FONTS.tight }}>
            Back to sign in
          </button>
        </p>
      </AuthLayout>
    )
  }

  const pErr = touched.pw ? validators.password(pw) : ''
  const cErr = touched.confirm && confirm !== pw ? 'Passwords do not match' : ''
  const valid = !validators.password(pw) && confirm === pw && confirm.length > 0

  const handleSubmit = async (e) => {
    e.preventDefault()
    setTouched({ pw: true, confirm: true })
    if (!valid) return
    setFormErr('')
    setLoading(true)
    try {
      await authApi.resetPassword(token, pw)
      setDone(true)
    } catch (err) {
      setFormErr(err.response?.data?.error || 'Something went wrong. Your link may have expired.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout screen="reset">
      {done ? (
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, border: `2px solid ${JAI_COLORS.success}`, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'jaiPop 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
              <CheckIcon size={26} stroke={JAI_COLORS.success} sw={2.6} />
            </div>
          </div>
          <FormHeader title="Password updated" />
          <p style={{ fontSize: 13.5, color: JAI_COLORS.mute, margin: '-14px 0 0', fontFamily: JAI_FONTS.tight, lineHeight: 1.6 }}>
            Your password has been changed. Use it next time you sign in.
          </p>
          <div style={{ marginTop: 22 }}>
            <PrimaryButton type="button" onClick={() => navigate('/login')}>Back to sign in</PrimaryButton>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <button type="button" onClick={() => navigate('/login')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: JAI_COLORS.mute, fontSize: 12.5, fontFamily: JAI_FONTS.tight, padding: 0, marginBottom: 14 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = JAI_COLORS.navy)} onMouseLeave={(e) => (e.currentTarget.style.color = JAI_COLORS.mute)}>
            <ArrowLeft size={15} /> Back to sign in
          </button>
          <FormHeader title="Set a new password" sub="Choose a strong password you haven't used before." />
          <ErrorBanner>{formErr}</ErrorBanner>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <PasswordField label="New password" value={pw} autoFocus autoComplete="new-password"
              onChange={setPw} onBlur={() => setTouched((t) => ({ ...t, pw: true }))}
              placeholder="Create a password" error={pErr} valid={!validators.password(pw)} showStrength />
            <PasswordField label="Confirm new password" value={confirm} autoComplete="new-password"
              onChange={setConfirm} onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
              placeholder="Re-enter your password" error={cErr} valid={confirm.length > 0 && confirm === pw} />
          </div>
          <div style={{ marginTop: 12 }}>
            <PrimaryButton loading={loading}>{loading ? 'Updating…' : 'Update password'}</PrimaryButton>
          </div>
        </form>
      )}
    </AuthLayout>
  )
}
